import type { ReactNode } from "react";

/**
 * Naturlig länkning i forumtexter.
 *
 * - @omnämnanden markeras
 * - riktiga URL:er och bara-domäner (fyndanu.se, akhusbilfritid.se) blir klickbara
 * - ett fåtal naturliga fraser länkas till relevanta sidor (max 2 per inlägg)
 *   så att texten aldrig ser spammig ut.
 */

const FYNDANU_STORE =
  "https://fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840";

type PhraseRule = { phrases: string[]; href: string; external?: boolean };

const PHRASE_RULES: PhraseRule[] = [
  {
    phrases: ["fyndanu.se", "fyndanu"],
    href: "https://fyndanu.se",
    external: true,
  },
  {
    phrases: ["sälja husbilen", "sälja min husbil", "sälja husvagnen", "annonsera husbil"],
    href: "https://fyndanu.se/salj-husbil",
    external: true,
  },
  {
    phrases: ["husbilar till salu", "husvagnar till salu", "begagnade husbilar"],
    href: FYNDANU_STORE,
    external: true,
  },
  { phrases: ["vårt lager", "fordon i lager"], href: "/fordon" },
  { phrases: ["fukttest", "gasoltest"], href: "/fordon" },
  { phrases: ["reservdelar"], href: "/reservdelar" },
  { phrases: ["hitta rätt husbil", "husbilsmatchning"], href: "/husbil-matchning" },
];

const MAX_PHRASE_LINKS = 2;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type Token =
  | { kind: "text"; value: string }
  | { kind: "mention"; value: string }
  | { kind: "link"; value: string; href: string; external: boolean };

const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>()]+|(?:fyndanu|akhusbilfritid)\.(?:se|com)(?:\/[^\s<>()]*)?/giu;
const MENTION_RE = /@[\p{L}0-9][\p{L}0-9_.-]{1,30}/gu;

function normalizeHref(raw: string) {
  const cleaned = raw.replace(/[.,;:!?)]+$/u, "");
  const href = /^https?:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`;
  return { text: cleaned, href, trailing: raw.slice(cleaned.length) };
}

/** Bygger tokens: URL:er först, sedan @omnämnanden, sist naturliga fraser. */
export function tokenizeForumBody(body: string): Token[] {
  if (!body) return [];

  type Match = { start: number; end: number; token: Token };
  const matches: Match[] = [];
  const taken: Array<[number, number]> = [];
  const overlaps = (s: number, e: number) =>
    taken.some(([ts, te]) => s < te && e > ts);
  const claim = (s: number, e: number) => taken.push([s, e]);

  for (const m of body.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const { text, href } = normalizeHref(m[0]);
    const end = start + text.length;
    if (overlaps(start, end)) continue;
    claim(start, end);
    matches.push({ start, end, token: { kind: "link", value: text, href, external: true } });
  }

  for (const m of body.matchAll(MENTION_RE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (overlaps(start, end)) continue;
    claim(start, end);
    matches.push({ start, end, token: { kind: "mention", value: m[0] } });
  }

  let phraseLinks = 0;
  for (const rule of PHRASE_RULES) {
    if (phraseLinks >= MAX_PHRASE_LINKS) break;
    const sorted = [...rule.phrases].sort((a, b) => b.length - a.length);
    let hit = false;
    for (const phrase of sorted) {
      if (hit) break;
      const re = new RegExp(`(?<![\\p{L}0-9@/.-])(${escapeRegExp(phrase)})(?![\\p{L}0-9-])`, "iu");
      const m = re.exec(body);
      if (!m) continue;
      const start = m.index;
      const end = start + m[0].length;
      if (overlaps(start, end)) continue;
      claim(start, end);
      matches.push({
        start,
        end,
        token: {
          kind: "link",
          value: m[0],
          href: rule.href,
          external: rule.external === true,
        },
      });
      hit = true;
      phraseLinks++;
    }
  }

  matches.sort((a, b) => a.start - b.start);

  const tokens: Token[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) tokens.push({ kind: "text", value: body.slice(cursor, m.start) });
    tokens.push(m.token);
    cursor = m.end;
  }
  if (cursor < body.length) tokens.push({ kind: "text", value: body.slice(cursor) });
  return tokens;
}

export function ForumBody({ body, className }: { body: string; className?: string }) {
  const tokens = tokenizeForumBody(body);
  return (
    <div className={className ?? "whitespace-pre-wrap break-words leading-relaxed text-foreground/90"}>
      {tokens.map((t, i): ReactNode => {
        if (t.kind === "mention") {
          return (
            <span key={i} className="font-semibold text-accent">
              {t.value}
            </span>
          );
        }
        if (t.kind === "link") {
          return t.external ? (
            <a
              key={i}
              href={t.href}
              target="_blank"
              rel="noopener nofollow"
              className="font-medium text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
            >
              {t.value}
            </a>
          ) : (
            <a
              key={i}
              href={t.href}
              className="font-medium text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
            >
              {t.value}
            </a>
          );
        }
        return <span key={i}>{t.value}</span>;
      })}
    </div>
  );
}
